import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import AnimalGrid from "components/animalgrid";
import BodyText from "components/bodytext";
import ContentBox from "components/contentbox";
import HeaderText from "components/headertext";
import PageGallery from "components/pagegallery";
import ratsnakesBook from "img/ckrs/TheAsianRatsnakesBook.jpg";
import dmexotics1 from "img/ckrs/dmexotics1.jpg";
import husbandryImg from "img/ckrs/Chinese King Rat Snakes.png";
import Image from "components/image";
import Anchor from "components/anchor";
import collectionMeta from "content/collections/chinese-king-ratsnakes.json";
import { animalGridFromJson } from "hooks/useGallery";

const animalGridData = animalGridFromJson(collectionMeta);

const ChineseKingRatsnake = () => {
  return (
    <ContentBox>
      <Helmet>
        <title>{collectionMeta.commonName}</title>
        <meta
          name="description"
          content="See the Chinese King Ratsnakes (Elaphe carinata)
            that are being kept here and learn more about their captive husbandry."
        />
        <link rel="canonical" href="https://www.gcreptiles.com/chinese-king-ratsnakes" />
      </Helmet>
      <HeaderText
        title={collectionMeta.commonName}
        subtitle={collectionMeta.scientificName}
      />
      <AnimalGrid data={animalGridData} />
      <BodyText>
        For a long time I have had the urge to get at least one species of Asian
        colubrid, so I decided on "the Stinking Goddess", aka the Chinese King
        Ratsnake. I sort of blame Dan Mulleary for this. He made{" "}
        <Anchor href="https://www.youtube.com/watch?v=5Ox8oHYxI9c">
          this video
        </Anchor>{" "}
        where he is unboxing several of these and a couple of the snakes are
        especially big, beautiful, and of course, very calm. So that became my
        target species. (I ignored the fact that the animals were probably cold,
        and thus calm, from the shipping process.) Dan is great to watch by the
        way since he does so much work with Asian species.
      </BodyText>
      <Image
        src={dmexotics1}
        caption="Dan Mulleary with a large Chinese King Ratsnake"
      />
      <BodyText>
        I bought a pair of CKRs and they quickly showed me their true
        personalities. First of all they don't smell particularly bad like I was
        expecting. I mean it's not as good as the smell of baked bread, but it's
        not worse than the other stinky colubrids that I keep. The Black
        ratsnakes that I used to keep a long time ago were particularly
        foul-smelling.
      </BodyText>
      <BodyText>
        In terms of handleability the male is slightly easier to handle in that
        I don't get bitten by him every two seconds like I do when handling the
        female. The male just tries to squirm away which is not surprising. But
        at least he only bites occasionally.
      </BodyText>
      <BodyText>
        The female, who quickly earned the name Malice, doesn't stop biting when
        she's in my hands. I've tried things like getting her out once or twice
        a week consistently for a few minutes at a time to try and have some
        positive handling experiences with her so that she can learn that I'm
        not here to hurt her but so far I haven't noticed any changes in her
        behavior. When we have these handling sessions I talk out loud to her
        hoping that she becomes accustomed to my sight<i> and </i>sound. I
        sarcastically announce, "Malice, it's playtime!" and maybe she senses
        the sarcasm and it just angers her more. I don't know. Playtime has
        become her nickname though and I'm sure that she hates that name, which
        encourages me to use it often.
      </BodyText>
      <BodyText>
        Here is a great book to get to learn more about Asian ratsnakes. I got
        it and was surprised at how many species of Asian ratsnakes I wanted
        after reading it. So consider yourself warned. Books like this should
        come with a 10% off coupon that's good at breeders' online stores.
      </BodyText>
      <Image
        src={ratsnakesBook}
        caption="The Asian Ratsnakes and Kin of Greater China, by Kevin R Messenger"
        width="300"
      />
      <BodyText>
        Any ways, my Chinese King Ratsnakes are beautiful snakes and I love
        having them if for no other reason than getting that periodic adrenaline
        spike when I get them out. And when she is not being tormented by me,
        Malice will roam the plants and logs in her cage all day and poke her
        head out over the ledge and watch me as I work. It's both endearing and
        creepy to see her staring at me with those evil eyes. I<i> am </i>
        nervous about having 7- or 8-foot long death machines in my collection
        in a few years so I do hope eventually they get more tractable. For now,
        there is a lock on their cages so that no one accidentally opens their
        cages when I'm not around.
      </BodyText>

      <h1>Husbandry</h1>
      <Image src={husbandryImg} />
      <p>
        See{" "}
        <Link to="/husbandry-charts">how to read the husbandry charts</Link>.
      </p>
      
      <BodyText>
        <h3>Temperatures, Humidity, and Light</h3>
        <p>
          As you can see in the chart above, Chinese King Rat snakes do not like 
          hot temperatures. They do very well with temperatures in the 70s during 
          the day and low 70s or even upper 60s at night. I have observed mine
          basking under their heat panels during the day after they eat, so I 
          make sure they have a place that is a bit warmer when they are trying
          to digest a meal. 
        </p>
        <p>
          For humidity, my room naturally stays in the 40-50% humidity range and
          this seems to be sufficient for them most of the time. However, when 
          they are in shed, once a week or so I wet down the 4" of coconut 
          bark that they have as bedding. I also give the female a humid hide 
          box from March through August to help her feel secure, humid, and to
          have a place to lay here eggs.
        </p>
      </BodyText>

      <BodyText>
        <h3>Feeding</h3>
        <p>
          As the chart shows, I feed the female weekly from March through October.
          As a 6-7' long snake, I am feeding her medium rats. I know she could eat
          more, and more often too, but the one-medium-rat-per-week routine seems to 
          keep her at the right girth without her getting fat. The male only gets 
          fed a medium rat every other week.
        </p>
      </BodyText>

      <BodyText>
        <h3>Breeding</h3>
        <p>
          I've successfully bred my Chinese king rat snakes for several consecutive years
          using a cooling and brumation technique commonly used with colubrid species
          from cooler climates.
        </p>
        <p>
          I stop feeding in the third week of October, then gradually lower their
          temperatures over the following month. By the end of November, I move them
          to a room in my basement that stays cool and completely dark throughout the
          winter. Temperatures remain fairly consistent at 50–55°F, with the lights
          kept off.
        </p>
        <p>
          I keep the male and female separately during this period and minimize
          disturbances. Every two weeks, I briefly check their enclosures and replace
          their water with fresh water.
        </p>
        <p>
          In February I remove them from the room in the basement and gradually increase
          the temperatures until they are back to their normal temperatures. I feed the 
          female once, and then a day or two later I introduce the male to her cage. 
          I keep them together for the next four months or until I can tell that she is
          gravid, but I separate them for a day or so when I feed them.
        </p>
        <p>
          My female has been laying a clutch of 12-15 eggs in mid-May which I incubate at
          about 79-80 F. At this temperature, the eggs take 60 day to hatch. My female 
          has also laid a second clutch of eggs each summer too but none of those eggs
          have been fertile.
        </p>
        <p>
          I start my baby Chinese king rat snakes on thawed fuzzy mice. I've found that 
          their ontogenetic color change—from olive-green juvenile coloration to their 
          striking black-and-yellow adult pattern—seems to be driven more by growth and s
          ize than by age alone.
        </p>
        <p>
          For example, when raising clutch-mates, I've noticed that a baby that feeds 
          more consistently and grows faster will typically develop its adult coloration 
          sooner than a sibling of the same age that is a reluctant or inconsistent feeder.
        </p>
      </BodyText>

      <PageGallery slug="chinese-king-ratsnakes" maxWidth="900" />
    </ContentBox>
  );
};

export default ChineseKingRatsnake;
